'use client';

import i18next from 'i18next';
import { Check, ChevronsUpDown } from 'lucide-react';
import * as React from 'react';

import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import useMediaQuery from '@/hooks/useMediaQuery';
import { cn } from '@/lib/utils';

import { Drawer, DrawerContent, DrawerTitle, DrawerTrigger } from './drawer';
import { FormControl } from './form';

export type Option = { label: string; value: string };
export type OptionGroup = { title?: string; empty?: string; options: Option[] };

interface ComboBoxResponsiveProps {
  drawerTitle: string;
  groups: OptionGroup[];
  value: string | number | undefined;
  onChange: (value: string) => void;
  form?: boolean;
  className?: string;
}

function ComboBoxResponsive({ drawerTitle, groups, value, onChange, form, className }: ComboBoxResponsiveProps) {
  const [comboboxOpen, setComboboxOpen] = React.useState(false);
  const isDesktop = useMediaQuery('(min-width: 768px)');

  // Resolve the selected label from either flat options or grouped options
  const selectedLabel = React.useMemo(() => {
    if (!value) return undefined;
    const fromGroups = groups?.flatMap(g => g.options).find(option => option.value === value)?.label;
    return fromGroups;
  }, [value, groups]);

  if (isDesktop) {
    return (
      <Popover open={comboboxOpen} onOpenChange={setComboboxOpen}>
        <PopoverTrigger asChild>
          {form ? (
            <FormControl>
              <Button
                variant="outline"
                size="lg"
                role="combobox"
                className={cn('justify-between overflow-x-clip', !value && 'text-muted-foreground', className)}
              >
                {selectedLabel ?? i18next.t('placeholders.select')}
                <ChevronsUpDown className="opacity-50" />
              </Button>
            </FormControl>
          ) : (
            <Button
              variant="outline"
              size="lg"
              role="combobox"
              className={cn('justify-between overflow-x-clip', !value && 'text-muted-foreground', className)}
            >
              {selectedLabel ?? i18next.t('placeholders.select')}
              <ChevronsUpDown className="opacity-50" />
            </Button>
          )}
        </PopoverTrigger>
        <PopoverContent className="p-0" align="start">
          <ComboboxCommand
            drawerTitle={drawerTitle}
            groups={groups}
            value={value}
            onChange={onChange}
            setComboboxOpen={setComboboxOpen}
          />
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Drawer open={comboboxOpen} onOpenChange={setComboboxOpen}>
      <DrawerTrigger asChild>
        <Button
          variant="outline"
          size="lg"
          role="combobox"
          className={cn('justify-between overflow-x-clip', !value && 'text-muted-foreground', className)}
        >
          {selectedLabel ?? i18next.t('placeholders.select')}
          <ChevronsUpDown className="opacity-50" />
        </Button>
      </DrawerTrigger>
      <DrawerContent className="ml-2 mr-2">
        <div className="px-4 pt-4 flex justify-center">
          <DrawerTitle>{drawerTitle}</DrawerTitle>
        </div>
        <ComboboxCommand
          drawerTitle={drawerTitle}
          groups={groups}
          value={value}
          onChange={onChange}
          setComboboxOpen={setComboboxOpen}
          searchInputPosition="bottom"
          className="mt-4 border-t"
        />
      </DrawerContent>
    </Drawer>
  );
}

interface ComboboxProps extends ComboBoxResponsiveProps {
  setComboboxOpen: (open: boolean) => void;
  className?: string;
  searchInputPosition?: 'top' | 'bottom';
}

export function ComboboxCommand({
  groups,
  value,
  onChange,
  setComboboxOpen,
  className,
  searchInputPosition = 'top'
}: ComboboxProps) {
  return (
    <Command className={cn('justify-end', className)}>
      {searchInputPosition === 'top' && (
        <CommandInput placeholder={i18next.t('placeholders.search')} className="h-10" />
      )}
      <CommandList>
        <CommandEmpty>{i18next.t('placeholders.search_failure')}</CommandEmpty>
        {groups.map((group, gi) => (
          <CommandGroup key={gi} heading={group.title}>
            {group.options.length === 0 && !!group.empty ? (
              <div className="text-center text-sm text-muted-foreground">{group.empty}</div>
            ) : (
              group.options.map((option, oi) => (
                <CommandItem
                  value={`${option.value}__${option.label}__${gi}__${oi}`}
                  key={`${option.value}__${option.label}__${gi}__${oi}`}
                  keywords={[option.label]}
                  className="min-h-10"
                  onSelect={() => {
                    onChange(option.value);
                    setComboboxOpen(false);
                  }}
                >
                  {option.label}
                  <Check className={cn('ml-auto', option.value === value ? 'opacity-100' : 'opacity-0')} />
                </CommandItem>
              ))
            )}
          </CommandGroup>
        ))}
      </CommandList>
      {searchInputPosition === 'bottom' && (
        <div className="border-t">
          <CommandInput placeholder={i18next.t('placeholders.search')} className="h-10 text-base" />
        </div>
      )}
    </Command>
  );
}

export { ComboBoxResponsive };
